import React from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComment,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';

/**
 * Portal component for song options menu
 * 
 * @param {boolean} show - Whether to show the portal
 * @param {object} position - Position coordinates {x, y}
 * @param {function} onClose - Function to call when closing the menu
 * @param {function} onAddComment - Function to handle add/edit comment action
 * @param {function} onShowNotes - Function to handle show notes action
 * @param {boolean} hasComment - Whether the song has a comment
 * @param {boolean} hasNotes - Whether the song has notes
 * @returns {JSX.Element|null} Portal component or null if hidden
 */
const SongOptionsPortal = ({
  show,
  position,
  onClose,
  onAddComment,
  onShowNotes,
  hasComment,
  hasNotes
}) => {
  const { t } = useLanguage();
  if (!show) return null;
  
  // Calculate safe position that stays on screen
  const calculateSafePosition = () => {
    // Get window dimensions
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Menu dimensions (approximate)
    const menuWidth = 200;
    const menuHeight = hasNotes ? 100 : 50; // Adjust based on number of options
    
    // Calculate position that keeps menu on screen
    let left = position.x - menuWidth; // Position to the left of the icon by default
    let top = position.y;
    
    // Check right edge
    if (left + menuWidth > windowWidth - 10) {
      left = windowWidth - menuWidth - 10; // Keep 10px from right edge
    }
    
    // Check left edge
    if (left < 10) {
      left = 10; // Keep 10px from left edge
    }
    
    // Check bottom edge
    if (top + menuHeight > windowHeight - 10) {
      top = position.y - menuHeight; // Position above the icon
    }
    
    return { left, top };
  };
  
  const safePosition = calculateSafePosition();
  
  return createPortal(
    <>
      {/* Backdrop for greying out the app */}
      <div className="song-menu-backdrop" onClick={onClose}></div>
      
      {/* The actual menu popup with safe positioning */}
      <div 
        className="song-options-portal" 
        style={{ 
          top: safePosition.top + 'px', 
          left: safePosition.left + 'px' 
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddComment();
          }}
        >
          <FontAwesomeIcon icon={faComment} className="me-2" />
          {hasComment ? t('events.setlist.editComment') : t('events.setlist.addComment')}
        </button>

        {hasNotes && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onShowNotes();
            }}
          >
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            {t('events.setlist.showNotes')}
          </button>
        )}
      </div>
    </>,
    document.body
  );
};

export default SongOptionsPortal;